import { Component, Input, SimpleChanges, LOCALE_ID, AfterViewInit, ViewChild, ElementRef, Output, EventEmitter  } from '@angular/core';
import { Event, Farm, Organisation } from '../../../models/types';
import { CommonModule, DatePipe, registerLocaleData  } from '@angular/common';
import { faCalendar, faPlus, faUser, faTrash, faStar, faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MarketPageService } from '../../services/market-page.service';
import { LoginService } from '../../services/login.service';
import localeSl from '@angular/common/locales/sl';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ProfilePageService } from '../../services/profile-page.service';
import { EventService } from '../../services/event.service';
import truncate from 'html-truncate';
import { imageLocation } from '../../../environments/environment.development';
import Swal from 'sweetalert2';
import { RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddEventComponent } from '../add-event/add-event.component';

registerLocaleData(localeSl);

@Component({
  selector: 'app-event-list',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, RouterModule, AddEventComponent],
  templateUrl: './event-list.component.html',
  styleUrl: './event-list.component.scss',
  providers: [{ provide: LOCALE_ID, useValue: 'sl' }, DatePipe]
})
export class EventListComponent {
  @ViewChild('eventsRow') eventScrollRow!: ElementRef;
  showScrollHint = true; 


  faCalendar = faCalendar;
  faUser = faUser;
  faPlus = faPlus;
  faTrash = faTrash;
  faStar = faStar;
  faPen = faPen;

  displayedEvents: Event[] = []; // Events to be displayed initially
  showLoadMoreButton = true; // Show "Load More" button only when there are more events to load
  currentPage = 1; // Track which set of events we are currently displaying
  eventsPerPage = 6; // Number of events to display per page
  additionalEvents = 3; // Number of events to load each time the button is clicked

  organisations: Organisation[] = [];
  
  @Input() allEvents: Event[] = [];
  @Input() allFarms: Farm[] = [];
  @Input() onAdminPage: boolean = false;
  @Input() onOrganisationPage: boolean = false;
  @Input() isOrgAdmin: boolean = false;
  @Input() isSuperAdmin: boolean = false;
  @Input() organisationID: string = "-1";
  @Input() onCalendarPage: boolean = false;

  @Output() eventChanged = new EventEmitter<Event>();
  
  private farms: { [key: number]: string } = {};


  constructor(
    private marketPageService: MarketPageService,
    private loginService: LoginService,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer,
    private profilePageService: ProfilePageService,
    private eventService: EventService,
    private modalService: NgbModal
  ) {}
  

  ngOnInit() {
    this.loadOrganisations();
    if (this.allEvents.length > 0) {
      this.allEvents = this.assignEventImages(this.allEvents);
      this.allEvents = this.assignOrganisationNames(this.allEvents);
      this.allEvents = this.filterAndSortEvents(this.allEvents);
      this.displayInitialEvents();
    }
  }

  displayInitialEvents() {
    this.displayedEvents = this.allEvents.slice(0, this.eventsPerPage);
    console.log('Initial events:', this.displayedEvents);
    if (this.allEvents.length > this.eventsPerPage) {
      this.showLoadMoreButton = true;
    } else {
      this.showLoadMoreButton = false;
    }
  }

  loadMoreEvents() {
    const nextEvents = this.allEvents.slice(
      this.displayedEvents.length,
      this.displayedEvents.length + this.additionalEvents
    );
    this.displayedEvents = [...this.displayedEvents, ...nextEvents];

    // Hide the "Load More" button if there are no more events to load
    if (this.displayedEvents.length >= this.allEvents.length) {
      this.showLoadMoreButton = false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['allEvents']) {
      // 1. Reset pagination state when input events change
      this.currentPage = 1; 
      
      // 2. Process and filter/sort the entire new list
      this.allEvents = this.assignEventImages(this.allEvents);
      this.allEvents = this.assignOrganisationNames(this.allEvents);
      this.allEvents = this.filterAndSortEvents(this.allEvents);

      // 3. Display the first page of the newly processed list
      this.displayInitialEvents();
    }
    if (changes['allFarms'] && !changes['allFarms'].firstChange) {
      this.updateFarmNames();
    }
  }

  formatDate(date: string, format: string): string {
    return this.datePipe.transform(date, format, 'sl') || '';
  }

  getTruncatedProductDescription(description: string): SafeHtml {
    const truncatedHtml = truncate(description, 200, { keepImageTag: true });
    return this.sanitizer.bypassSecurityTrustHtml(truncatedHtml);
  }

  openEventDetails(event: MouseEvent, myEvent: Event) {
    event.preventDefault();

    const normalizedTitle = myEvent.title.toLowerCase().replace(/\s+/g, "");

    if (normalizedTitle.includes("hot100")) {
      this.scrollToSection('hot100');
      return;
    }

    if (normalizedTitle.includes("tekprijateljstva")) {
      this.scrollToSection('tek-prijateljstva');
      return;
    }

    this.profilePageService.openEventDetailsModal(myEvent);
  }

  private scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  loadOrganisations() {
    this.loginService.getAllOrganisations().subscribe({
      next: (response) => {
        console.log('Raw response:', response);
        if (response.body && Array.isArray(response.body.organisations)) {
          this.organisations = response.body.organisations;
          this.allEvents = this.assignOrganisationNames(this.allEvents);
        } else {
          console.error('Unexpected response structure:', response.body);
        }
        console.log('Organisations fetched:', this.organisations);
      },
      error: (err) => {
        console.error('Error fetching organisations:', err);
      }
    });
  }

  private updateFarmNames() {
    // Ensure that farmID is correctly mapped to farmName
    this.farms = this.allFarms.reduce((map, farm) => {
      map[farm.farmID] = farm.name;
      return map;
    }, {} as { [key: number]: string }); // Explicitly specify the type here
    
    // Update events with farm names if necessary
    this.allEvents = this.assignOrganisationNames(this.allEvents);
  }

  private assignOrganisationNames(events: Event[]): Event[] {
    return events.map(event => {
      if (event.organisationID === -1 && this.farms[event.farmID]) {
        event.organisationName = this.farms[event.farmID]; // Use farmName if organisationID is -1
      } else {
        const organisation = this.organisations.find(org => org.organisationID === event.organisationID);
        event.organisationName = organisation ? organisation.name : 'Unknown';
      }
      return event;
    });
  }

  private assignEventImages(events: Event[]): Event[] {
    return events.map(event => {
      let defaultImageUrl = '../../../assets/images/generic/event.jpg';
      event.imageRef = event.imageRef ? 
        `${imageLocation}/static/content/events/${event.eventID}/${event.imageRef}` :
        defaultImageUrl;
      return event;
    });
  }

  private filterAndSortEvents(events: Event[]): Event[] {
    const currentDate = new Date();
    return events
      .filter(event => new Date(event.end) >= currentDate)
      .sort((a, b) => {
        if (a.exposed && !b.exposed) {
          return -1; // Exposed events come first
        } else if (!a.exposed && b.exposed) {
          return 1; // Non-exposed events come after
        } else if (a.exposed && b.exposed) {
          // If both are exposed, sort by start date
          return new Date(a.start).getTime() - new Date(b.start).getTime();
        } else {
          // If neither is exposed, sort by start date
          return new Date(a.start).getTime() - new Date(b.start).getTime();
        }
      });
  }
  openEventAdd() {
    this.marketPageService.openEventAddModal(this.isOrgAdmin, this.organisationID);
    this.marketPageService.eventAdded$.subscribe(() => {
      this.eventChanged.emit(); // Fire the signal after event is added
    });
  }

  deleteEvent(event: Event) {
    this.eventService.deleteEvent(event.eventID).subscribe({
      next: () => {
        console.log('Event deleted:', event);
        this.eventChanged.emit()
        this.displayedEvents = this.displayedEvents.filter(e => e.eventID !== event.eventID);
        Swal.fire({
          icon: 'success',
          title: 'Izbrisano!',
          text: 'Dogodek ste uspešno izbrisali.',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });
      },
      error: (err) => {
        console.error('Error deleting event:', err);
      }
    });
  }

  ngAfterViewInit() {
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        this.animateScroll();
        observer.disconnect(); // Only trigger once
      }
    }, { threshold: 0.5 }); // Trigger when 50% of the row is visible

    observer.observe(this.eventScrollRow.nativeElement);
  }

  animateScroll() {
    const scrollElement = this.eventScrollRow.nativeElement;

    // Small right scroll
    scrollElement.scrollBy({ left: 500, behavior: 'smooth' });

    // Scroll back left after a short delay
    setTimeout(() => {
      scrollElement.scrollBy({ left: -500, behavior: 'smooth' });
    }, 800); // Wait 0.8s before scrolling back
  }

  toggleExposeEvent(eventID: string) {
    const eventid = parseInt(eventID);
    this.eventService.toggleExposeEvent(eventid).subscribe({
      next: () => {
        this.eventChanged.emit()
        Swal.fire({
          icon: 'success',
          title: 'Uspeh!',
          text: 'Dogodek je bil uspešno posodobljen.',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });
      },
      error: (err) => {
        Swal.fire({
          icon: 'error',
          title: 'Napaka!',
          text: 'Dogodka ni bilo mogoče posodobiti.',
          timer: 2000,
          timerProgressBar: true,
          showConfirmButton: false
        });
        console.error('Error toggling event exposure:', err);
      },
    });
  }

  openEventEdit(event: Event) {
    const modalRef = this.modalService.open(AddEventComponent, { size: 'lg', backdrop: 'static' });
    modalRef.componentInstance.isEditMode = true;
    modalRef.componentInstance.existingEvent = event;
    modalRef.componentInstance.organisationID = this.organisationID;
    modalRef.componentInstance.isOrgAdmin = this.isOrgAdmin;

    modalRef.result.then((result) => {
      this.eventChanged.emit(); 
    }).catch(() => {
    });
  }
}
